import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { upperFirst } from 'lodash'
import moment from 'moment'
import { css } from '@emotion/core'

import { OperationLogType } from '@/models/operation-log'
import {
  operationLogCellEllipsisStyle,
  operationLogTdDatetimeStyle,
  operationLogTdEllipsisStyle,
  operationLogTdNowrapStyle,
} from '@/components/organisms/operation-log/table-cell-styles'

/** 旧操作ログ一覧行（展開で json を整形表示）。列: 操作日時・操作・機能・対象データ・アカウント・スタッフ */
export const OperationLogLegacyRow: React.FC<{ item: OperationLogType }> = ({ item: operation }) => {
  const { t, i18n } = useTranslation()
  const [showDetail, setShowDetail] = useState(false)

  const getLogTranslation = (key: string) => {
    const trimmed = (key ?? '').toString().trim()
    if (!trimmed) return '-'
    if (i18n.exists(`OperationLog.itemLabel.${trimmed}`)) return t(`OperationLog.itemLabel.${trimmed}`)
    if (i18n.exists(`OperationLog.${trimmed}`)) return t(`OperationLog.${trimmed}`)
    return t(trimmed)
  }

  const formatJsonDetail = (jsonStr: string | null | undefined): string => {
    if (jsonStr == null || String(jsonStr).trim() === '') return '-'
    try {
      const target = JSON.parse(jsonStr) as unknown
      const transformAllUnixDate = (obj: unknown): void => {
        if (typeof obj === 'object' && obj !== null) {
          for (const key of Object.keys(obj as object)) {
            const v = (obj as Record<string, unknown>)[key]
            if (typeof v === 'object' && v !== null) {
              transformAllUnixDate(v)
            } else if (String(v).length === 10 && moment.unix(Number(v)).isValid()) {
              ;(obj as Record<string, unknown>)[key] = moment.unix(Number(v)).format('YYYY-MM-DD HH:mm:ss')
            }
          }
        }
      }
      transformAllUnixDate(target)
      return JSON.stringify(target, null, '\t')
    } catch {
      return jsonStr
    }
  }

  const detailText = formatJsonDetail(operation.json)

  const dateDisplay = (() => {
    const m = moment(operation.date)
    return m.isValid() ? m.format('YYYY/MM/DD HH:mm:ss') : operation.date || '-'
  })()

  return (
    <>
      <tr
        onClick={() => setShowDetail(!showDetail)}
        css={{ cursor: 'pointer', color: '#272727' }}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setShowDetail(!showDetail)
          }
        }}
      >
        <td css={[operationLogTdNowrapStyle, { paddingLeft: 28, width: '5%' }]}>
          <img
            src={require('@/static/images/arrow_right.svg')}
            alt=""
            css={{
              width: 32,
              height: 32,
              cursor: 'pointer',
              transform: `rotate(${showDetail ? -90 : 90}deg)`,
              transition: 'all 300ms',
            }}
          />
        </td>
        <td css={[operationLogTdDatetimeStyle, { width: '18%' }]}>{dateDisplay}</td>
        <td css={[operationLogTdEllipsisStyle, { width: '4%' }]}>
          <span css={operationLogCellEllipsisStyle} title={upperFirst(getLogTranslation(operation.contents)) || '-'}>
            {upperFirst(getLogTranslation(operation.contents)) || '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '11%' }]}>
          <span
            css={operationLogCellEllipsisStyle}
            title={getLogTranslation(operation.features === 'CheckIn' ? 'Accommodation' : operation.features) || '-'}
          >
            {getLogTranslation(operation.features === 'CheckIn' ? 'Accommodation' : operation.features) || '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '34%' }]}>
          <span css={operationLogCellEllipsisStyle} title={getLogTranslation(operation.table) || '-'}>
            {getLogTranslation(operation.table) || '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '16%' }]}>
          <span css={operationLogCellEllipsisStyle} title={operation.account ?? '-'}>
            {operation.account ?? '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '12%', paddingRight: 28 }]}>
          <span css={operationLogCellEllipsisStyle} title={operation.staff ?? '-'}>
            {operation.staff ?? '-'}
          </span>
        </td>
      </tr>
      {showDetail && (
        <tr>
          <td colSpan={7} css={detailRowTdStyle}>
            <div css={detailJsonBoxStyle}>{detailText}</div>
          </td>
        </tr>
      )}
    </>
  )
}

const detailRowTdStyle = css({
  padding: '28px 39px 28px 74px',
  backgroundColor: '#F5F5F5',
  overflowX: 'hidden',
  maxWidth: 0,
})

const detailJsonBoxStyle = css({
  padding: 28,
  backgroundColor: '#fff',
  borderRadius: 17,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  overflowX: 'hidden',
  maxWidth: '100%',
  color: '#272727',
  fontSize: 12,
})
